import { FormEvent, useEffect } from 'react'
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'

import Styles from './CardForm.module.scss'
import { Modal } from 'almanac-ui/Modal'

interface Props {
  isProcessing: boolean
  setIsProcessing: (isProcessing: boolean) => void
  onClose: () => void
  onPaymentMethodCreated: (paymentMethodId: string) => void
  buttonConfiguration: 'stand-alone' | 'multi-step'
  dataHeapId?: 'payment-start'
  allowCancel?: boolean
}

export const CardForm = ({
  isProcessing,
  setIsProcessing,
  onClose,
  onPaymentMethodCreated,
  buttonConfiguration,
  dataHeapId,
  allowCancel = true,
}: Props) => {
  const stripe = useStripe()
  const elements = useElements()

  const focusCard = () => {
    if (!elements) return

    const card = elements.getElement(CardElement)

    if (card) card.focus()
  }

  useEffect(() => {
    if (!elements) return

    const card = elements.getElement(CardElement)

    if (card) card.on('ready', focusCard)
  })

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) return

    const card = elements.getElement(CardElement)

    if (card === null) return

    setIsProcessing(true)

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card,
    })

    setIsProcessing(false)

    if (error) {
      showToastMessage(error.message, 'error')
      return
    }

    if (!paymentMethod) return

    onPaymentMethodCreated(paymentMethod.id)
  }

  return (
    <>
      <form onSubmit={handleSubmit} className={Styles.paymentForm}>
        <div className={Styles.paymentDetails}>
          <p className={Styles.paymentDetailsHeader}>Payment Details</p>
          <p className={Styles.creditCardLabel}>Credit or Debit Card</p>
          <CardElement className={Styles.cardField} />
        </div>
        <Modal.Footer>
          <Modal.FooterContent slot="left">
            <Modal.FooterButton size="lg" isDisabled={isProcessing} onPress={onClose} variant="tertiary">
              Cancel
            </Modal.FooterButton>
          </Modal.FooterContent>
          <Modal.FooterContent slot="right">
            <Modal.FooterButton type="submit" size="lg" data-testid="upgrade" isDisabled={isProcessing}>
              {isProcessing ? (
                <span>Processing...</span>
              ) : (
                <>
                  {buttonConfiguration === 'multi-step' && <span>Upgrade</span>}
                  {buttonConfiguration === 'stand-alone' && <span>Update</span>}
                  <span>&rarr;</span>
                </>
              )}
            </Modal.FooterButton>
          </Modal.FooterContent>
        </Modal.Footer>
      </form>
    </>
  )
}
